import { useEffect, useLayoutEffect } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useHotkeys } from 'react-hotkeys-hook';
import { useConfigurableHotkey } from '@/hooks/useConfigurableHotkey';
import { HOTKEY_IDS } from '@/lib/hotkeys/registry';
import { useGT } from 'gt-react';
import { cn } from '@/lib/utils';
import { useAgentPanel } from '@/context/agent-panel.context';
import { useAgentContainer } from '@/context/agent-container.context';
import { useAgentPanelEnabled } from '@/components/common/useAgentPanelEnabled';
import { AgentContainer } from '@/components/common/AgentContainer';
import { useNamiDefaultSurface } from './useNamiDefaultSurface';
import { useNamiInvalidationListener } from './useNamiInvalidationListener';
import { useNamiPanelLayout } from './useNamiPanelLayout';
import { useNamiPanelWidth } from './useNamiPanelWidth';
import { NamiResizeHandle } from './NamiResizeHandle';
import { isChatPath } from './isChatPath';
import { isDockTarget } from './isDockTarget';

/**
 * The right-hand Nami panel shell. Mounted once at the authenticated layout.
 *
 * Docked (wide viewports): the panel takes real estate from the page through
 * the `--agent-panel-width` variable `SidebarInset` pads by, so content shrinks
 * instead of being covered. Overlay (narrow viewports): the panel floats above
 * the page behind a dim backdrop and the variable stays at 0.
 *
 * Always mounted so the slide transition plays and the `AgentContainer` stays
 * registered; `inert` blocks focus and clicks while hidden. The chat UI itself
 * is portaled in by `ChatAgentRenderer`.
 */
export function NamiPanel() {
  const gt = useGT();
  const {
    isOpen,
    open,
    close,
    toggle,
    rememberSurfacePath,
    dockRequested,
    consumeDockRequest,
  } = useAgentPanel();
  const { effectiveModuleConfig } = useAgentContainer();
  const enabled = useAgentPanelEnabled(effectiveModuleConfig);
  const [width, setWidth] = useNamiPanelWidth();
  const mode = useNamiPanelLayout();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const onChat = isChatPath(pathname);

  useNamiDefaultSurface();
  useNamiInvalidationListener();

  const hasSurface = effectiveModuleConfig !== null;
  const visible = enabled && hasSurface && isOpen && !onChat;
  const docked = visible && mode === 'docked';

  // Sync the layout CSS variable that `SidebarInset` reads for its right
  // padding. `useLayoutEffect` so the DOM update happens before paint and the
  // inset padding never lags the panel by a frame. Reset on unmount so a
  // remount never inherits a stale width.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      '--agent-panel-width',
      docked ? `${String(width)}px` : '0px',
    );
    return () => {
      root.style.setProperty('--agent-panel-width', '0px');
    };
  }, [docked, width]);

  // The full-page chat is Nami already, and a page that lost its only surface
  // (a module-flagged page with Nami off) has nothing to show: drop the
  // open state so the pill returns and no phantom slot lingers.
  useEffect(() => {
    if (isOpen && (onChat || !hasSurface)) close();
  }, [isOpen, onChat, hasSurface, close]);

  // Where "Dock to side" on the full-page chat brings the conversation back
  // to: only pages that can actually show the panel (or the dock request would
  // wait forever), and never Home, which already has its own chat box.
  useEffect(() => {
    if (!onChat && hasSurface && isDockTarget(pathname)) {
      rememberSurfacePath(pathname);
    }
  }, [onChat, hasSurface, pathname, rememberSurfacePath]);

  // A dock request made on /chat is honoured once a page with a surface
  // renders, so it never races the close-on-chat rule above.
  useEffect(() => {
    if (dockRequested && !onChat && hasSurface) {
      open();
      consumeDockRequest();
    }
  }, [dockRequested, onChat, hasSurface, open, consumeDockRequest]);

  useConfigurableHotkey(
    HOTKEY_IDS.aiAssistant,
    () => {
      if (hasSurface) toggle();
    },
    { preventDefault: true, enabled: enabled && hasSurface && !onChat },
    [enabled, hasSurface, onChat, toggle],
  );

  useHotkeys(
    'escape',
    () => close(),
    { enabled: visible && mode === 'overlay', enableOnFormTags: true },
    [visible, mode, close],
  );

  if (!enabled) return null;

  return (
    <>
      {mode === 'overlay' && visible && (
        <div
          className="fixed inset-0 z-[49] bg-foreground/20"
          onClick={close}
          aria-hidden
        />
      )}
      <aside
        aria-label={gt('AI assistant', {
          context: 'accessible name of the AI assistant side panel',
        })}
        inert={!visible}
        data-capture-exclude
        data-state={visible ? 'open' : 'closed'}
        className={cn(
          'bg-background fixed inset-y-0 right-0 z-50 flex flex-col border-l transition-transform duration-200 ease-in-out motion-reduce:transition-none',
          visible ? 'translate-x-0' : 'translate-x-full',
          mode === 'overlay' && 'shadow-lg',
        )}
        style={{ width }}
      >
        <NamiResizeHandle
          onResize={(delta) => setWidth((current) => current + delta)}
        />
        <AgentContainer className="flex h-full min-h-0 w-full flex-col overflow-hidden" />
      </aside>
    </>
  );
}
